import React, { useMemo } from 'react';
import '../styles/components/MomentsPanel.css';
import '../styles/common/moments.css';
import SpeechItem from './SpeechItem';
import { SpeechText as ApiSpeechText } from '../types/api';
import { getMomentStyling } from '../utils/momentUtils';
import { MOMENT_ICONS, getSentimentIcon } from '../utils/iconMappings';
import SummaryTable, { SummaryTableColumn, SummaryTableRow, SummaryItem as SummaryStatItem } from './SummaryTable';

interface Moment {
  id: string;
  name: string;
  time: number;
  sentiment_id?: number;
  sentiment_name?: string;
}

interface MomentOccurrence {
  moment: Moment;
  speechText: ApiSpeechText;
}

interface MomentsPanelProps {
  moments: Moment[];
  speechTexts: ApiSpeechText[];
  onSpeechClick: (speechId: string) => void;
}

const MomentsPanel: React.FC<MomentsPanelProps> = ({ moments, speechTexts, onSpeechClick }) => {
  // Group moments by name and count occurrences, including the corresponding speech text
  const momentStats = useMemo(() => {
    const acc = speechTexts.reduce((grouped, speechText) => {
    speechText.transcript_mmts?.forEach(transcriptMoment => {
      const moment = moments.find(m => m.id === `moment-${transcriptMoment.id}`);
      if (!moment) return;

        if (!grouped[moment.name]) {
          grouped[moment.name] = {
          count: 0,
            occurrences: [] as MomentOccurrence[],
        };
      }

        const isDuplicate = grouped[moment.name].occurrences.some(
        occ => occ.speechText.id === speechText.id
      );

      if (!isDuplicate) {
          grouped[moment.name].count++;
          grouped[moment.name].occurrences.push({
          moment,
          speechText
        });
      }
    });
      return grouped;
    }, {} as Record<string, { count: number; occurrences: MomentOccurrence[] }>);
    return acc;
  }, [moments, speechTexts]);

  type MomentRowData = {
    name: string;
    count: number;
    occurrences: MomentOccurrence[];
    sentimentName: string;
    sentimentId?: number;
  };

  const momentRows = useMemo<MomentRowData[]>(() => {
    const entries = Object.entries(momentStats).map(([name, stats]) => {
      const first = stats.occurrences[0]?.moment;
      const sentimentName = first?.sentiment_name || '';
      const sentimentId = first?.sentiment_id ?? undefined;
      return { name, count: stats.count, occurrences: stats.occurrences, sentimentName, sentimentId };
    });

    return entries.sort((a, b) => a.name.localeCompare(b.name));
  }, [momentStats]);

  // Summary totals
  const summary = useMemo(() => {
    const totalOccurrences = momentRows.reduce((sum, item) => sum + item.count, 0);
    let pos = 0, neg = 0, neu = 0;
    momentRows.forEach(item => {
      const n = (item.sentimentName || '').toLowerCase();
      if (n.includes('positive')) pos += item.count;
      else if (n.includes('negative')) neg += item.count;
      else if (n.includes('neutral')) neu += item.count;
    });
    return { totalOccurrences, pos, neg, neu };
  }, [momentRows]);

  // CSV download
  const formatTimestamp = (st?: string, ms?: number) => {
    if (st && st.trim().length > 0) return st;
    if (typeof ms === 'number') {
      const totalSeconds = Math.floor(ms / 1000);
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${pad(h)}:${pad(m)}:${pad(s)}`;
    }
    return '';
  };
  const columns: SummaryTableColumn<MomentRowData>[] = [
    {
      key: 'sentiment',
      label: 'Sentiment',
      width: '96px',
      sortable: true,
      sortAccessor: row => (row.sentimentName || '').toLowerCase(),
      render: (row) => {
        const styling = getMomentStyling(row.sentimentId, row.sentimentName, true);
        return (
          <div className="moment-sentiment">
            {styling.icon && (
              <span className={`material-symbols-outlined moment-name-icon ${styling.className}`}>
                {styling.icon}
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (row) => (
        <div className="moment-name">
          <span>{row.name}</span>
        </div>
      ),
      sortAccessor: row => row.name.toLowerCase()
    },
    {
      key: 'count',
      label: 'Occurrences',
      width: '140px',
      sortable: true,
      align: 'right',
      render: (row) => <div className="moment-count">{row.count}</div>,
      sortAccessor: row => row.count
    }
  ];

  const createSentimentSummaryIcon = (sentiment: 'positive' | 'neutral' | 'negative') => {
    const styling = getMomentStyling(undefined, sentiment, true);
    return (
      <span className={`material-symbols-outlined moment-name-icon ${styling.className}`}>
        {getSentimentIcon(sentiment)}
      </span>
    );
  };

  const summaryItemsData: SummaryStatItem[] = [
    {
      id: 'total',
      value: summary.totalOccurrences,
      icon: <span className="material-symbols-outlined moment-name-icon">{MOMENT_ICONS.MENU}</span>,
      tooltip: 'Total moments occurrences'
    },
    {
      id: 'positive',
      value: summary.pos,
      icon: createSentimentSummaryIcon('positive'),
      tooltip: 'Positive moments'
    },
    {
      id: 'neutral',
      value: summary.neu,
      icon: createSentimentSummaryIcon('neutral'),
      tooltip: 'Neutral moments'
    },
    {
      id: 'negative',
      value: summary.neg,
      icon: createSentimentSummaryIcon('negative'),
      tooltip: 'Negative moments'
    }
  ];

  const tableRows: SummaryTableRow<MomentRowData>[] = momentRows.map(row => ({
    id: row.name,
    data: row,
    expandedContent: (
      <div className="moment-occurrences">
        {row.occurrences.map((occurrence, index) => (
          <SpeechItem
            key={`${occurrence.moment.id}-${index}`}
            id={occurrence.speechText.id?.toString() || `speech-${occurrence.speechText.start_ms}`}
            startMs={occurrence.speechText.start_ms}
            endMs={occurrence.speechText.end_ms}
            speech={occurrence.speechText.speech}
            speaker={{
              id: Number(occurrence.speechText.speaker_id),
              name: occurrence.speechText.speaker_name
            }}
            moments={occurrence.speechText.transcript_mmts || []}
            transcriptId={occurrence.speechText.transcript_id || 0}
            onClick={onSpeechClick}
            isActive={false}
            onMouseEnter={() => {}}
            remark={occurrence.speechText.remark || ''}
            reviewer={occurrence.speechText.reviewer || ''}
          />
        ))}
      </div>
    )
  }));

  const exportConfig = useMemo(() => ({
    enabled: true,
    filenameBase: 'transcript-moments',
    buildCsvRows: (rowsForExport: SummaryTableRow<MomentRowData>[]) => {
      const header = ['timestamp', 'moment_name', 'sentiment'];
      const csvRows: string[][] = [header];

      rowsForExport.forEach(row => {
        row.data.occurrences.forEach(({ speechText, moment }) => {
          const timestamp = formatTimestamp(speechText.start_time as any, speechText.start_ms as any);
          const sentiment = (moment.sentiment_name || row.data.sentimentName || '').toLowerCase();
          csvRows.push([timestamp, row.data.name, sentiment]);
        });
      });

      return csvRows;
    },
    pdf: {
      pageInfoText: `Total unique moments: ${momentRows.length}`,
      additionalContent: (pdfContainer: HTMLElement) => {
        const summarySection = document.createElement('div');
        summarySection.className = 'summary-table-pdf-summary';
        const summaryItemsForPdf = [
          { label: 'Total', value: summary.totalOccurrences },
          { label: 'Positive', value: summary.pos },
          { label: 'Neutral', value: summary.neu },
          { label: 'Negative', value: summary.neg }
        ];
        summaryItemsForPdf.forEach(item => {
          const itemDiv = document.createElement('div');
          itemDiv.className = 'summary-table-pdf-summary-item';
          itemDiv.textContent = `${item.label}: ${item.value}`;
          summarySection.appendChild(itemDiv);
        });
        pdfContainer.prepend(summarySection);
      },
      variants: [
        { label: 'PDF', filenameSuffix: 'collapsed' },
        { label: 'PDF (expanded)', filenameSuffix: 'expanded', expandAllRows: true }
      ]
    }
  }), [momentRows, summary]);

  return (
    <div className="moments-panel">
      <div className="moments-container">
        <SummaryTable
          summaryItems={summaryItemsData}
          columns={columns}
          rows={tableRows}
          defaultSort={{ key: 'name', direction: 'asc' }}
          exportConfig={exportConfig}
          className="moments-summary-table"
          searchPlaceholderEntity="moments"
        />
      </div>
    </div>
  );
};

export default MomentsPanel; 